import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { User, Mail, Settings, Bell, Shield } from "lucide-react";

export default function Profile() {
  const { user, plan } = useAuth();
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className="min-h-screen pt-24 pb-12 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Profile Settings</h1>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList>
            <TabsTrigger value="general" className="gap-2">
              <User className="w-4 h-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="plan" className="gap-2">
              <Settings className="w-4 h-4" />
              Plan Settings
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2">
              <Bell className="w-4 h-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="privacy" className="gap-2">
              <Shield className="w-4 h-4" />
              Privacy
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <Card className="p-6">
              <div className="flex items-center gap-6 mb-8">
                <div className="w-20 h-20 rounded-full bg-amber-500/10 flex items-center justify-center text-2xl font-bold text-amber-500">
                  {user?.name?.charAt(0) || user?.email?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2 className="font-semibold text-xl">
                    {user?.name || "Fitness Enthusiast"}
                  </h2>
                  <p className="text-muted-foreground flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {user?.email}
                  </p>
                </div>
              </div>

              <div className="grid gap-6">
                <div className="grid gap-2">
                  <Label htmlFor="name">Display Name</Label>
                  <Input
                    id="name"
                    placeholder="Your name"
                    defaultValue={user?.name || ""}
                    disabled={!isEditing}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ""}
                    disabled
                  />
                  <p className="text-xs text-muted-foreground">
                    Email cannot be changed
                  </p>
                </div>

                <div className="flex gap-4">
                  {isEditing ? (
                    <>
                      <Button
                        onClick={() => setIsEditing(false)}
                        className="bg-amber-500 text-black hover:bg-lime-500"
                      >
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditing(false)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={() => setIsEditing(true)}>
                      Edit Profile
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="plan">
            <Card className="p-6">
              <h2 className="font-semibold text-lg mb-6">Current Plan Settings</h2>
              
              {plan ? (
                <div className="space-y-6">
                  <div className="grid gap-4">
                    <div className="flex justify-between items-center p-4 bg-card/50 rounded-lg">
                      <div>
                        <p className="font-medium">Goal</p>
                        <p className="text-sm text-muted-foreground">{plan.overview.goal}</p>
                      </div>
                      <Button variant="outline" size="sm">Change</Button>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 bg-card/50 rounded-lg">
                      <div>
                        <p className="font-medium">Frequency</p>
                        <p className="text-sm text-muted-foreground">{plan.overview.frequency}</p>
                      </div>
                      <Button variant="outline" size="sm">Change</Button>
                    </div>
                    
                    <div className="flex justify-between items-center p-4 bg-card/50 rounded-lg">
                      <div>
                        <p className="font-medium">Split Type</p>
                        <p className="text-sm text-muted-foreground">{plan.overview.split}</p>
                      </div>
                      <Button variant="outline" size="sm">Change</Button>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4 border-t border-border">
                    <Button className="bg-amber-500 text-black hover:bg-lime-500" asChild>
                      <a href="/onboarding">Create New Plan</a>
                    </Button>
                    <Button variant="outline" asChild>
                      <a href="/plan">View Current Plan</a>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground mb-4">
                    You don't have an active plan yet.
                  </p>
                  <Button className="bg-amber-500 text-black hover:bg-lime-500" asChild>
                    <a href="/onboarding">Create Your Plan</a>
                  </Button>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <Card className="p-6">
              <h2 className="font-semibold text-lg mb-6">Notification Preferences</h2>
              <div className="space-y-4">
                {[
                  { id: "workout", label: "Workout Reminders", desc: "Get reminded about scheduled workouts" },
                  { id: "progress", label: "Progress Updates", desc: "Weekly progress summary" },
                  { id: "tips", label: "Fitness Tips", desc: "Occasional tips and advice" },
                ].map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-4 bg-card/50 rounded-lg">
                    <div>
                      <p className="font-medium">{item.label}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                    <input
                      type="checkbox"
                      defaultChecked
                      className="w-5 h-5 rounded border-border bg-background accent-amber-500"
                    />
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="privacy">
            <Card className="p-6">
              <h2 className="font-semibold text-lg mb-6">Privacy Settings</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-card/50 rounded-lg">
                  <div>
                    <p className="font-medium">Profile Visibility</p>
                    <p className="text-sm text-muted-foreground">Make your profile visible to others</p>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-border bg-background accent-amber-500"
                  />
                </div>
                
                <div className="flex items-center justify-between p-4 bg-card/50 rounded-lg">
                  <div>
                    <p className="font-medium">Share Progress</p>
                    <p className="text-sm text-muted-foreground">Allow sharing workout achievements</p>
                  </div>
                  <input
                    type="checkbox"
                    className="w-5 h-5 rounded border-border bg-background accent-amber-500"
                  />
                </div>

                <div className="pt-4 border-t border-border">
                  <Button variant="destructive">Delete Account</Button>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
